import { DatePicker, message, Table, Typography } from "antd";
import React, { useEffect, useState } from "react";
import { ICashTransferArchive } from "../../types/archiveCashTransfers.type";
import dayjs from "dayjs";
import { api } from "../../api/api";
import { roundToThreeDecimals } from "../../utils/roundToThreeDecimals";

const { Title } = Typography;
const { RangePicker } = DatePicker;

export default function ArchiveCashTransfers() {
  const [transfers, setTransfers] = useState<ICashTransferArchive[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs, dayjs.Dayjs] | undefined
  >([dayjs().startOf("month"), dayjs().endOf("month")]);

  const handleRangeChange = (dates: any) => {
    if (dates) {
      setDateRange([dates[0], dates[1]]);
    } else {
      setDateRange(undefined);
    }
  };

  const fetchTransfers = async () => {
    setLoading(true);
    try {
      const response = await api.get("/archive/cash-transfers", {
        params: {
          page,
          limit: pageSize,
          startDate: dateRange ? dateRange[0]?.format("DD.MM.YY") : undefined,
          endDate: dateRange ? dateRange[1]?.format("DD.MM.YY") : undefined,
        },
      });

      setTransfers(response.data.transfers);
      setTotal(response.data.total);
    } catch (error) {
      console.error(error);
      message.error("Ошибка получение конвертаций");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransfers();
  }, [page, pageSize, dateRange]);

  const columns = [
    {
      title: "Дата",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Дата удаления",
      dataIndex: "dateRemoved",
      key: "dateRemoved",
    },
    {
      title: "Удалил",
      dataIndex: "removedBy",
      key: "removedBy",
    },
    {
      title: "Дата обещанного перевода",
      dataIndex: "promisedDate",
      key: "promisedDate",
      render: (text: string | null | undefined) => {
        return text ? dayjs(text).format("DD-MM-YYYY") : "Н/Д";
      },
    },
    {
      title: "Клиент",
      dataIndex: "clientName",
      key: "clientName",
    },
    {
      title: "Валюта",
      dataIndex: "currency",
      key: "currency",
    },
    {
      title: "Сумма",
      dataIndex: "amount",
      key: "amount",
      render: (amount: number) => roundToThreeDecimals(amount),
    },
    {
      title: "Сумма к зачислению",
      dataIndex: "total",
      key: "total",
      render: (amount: number) => roundToThreeDecimals(amount),
    },
    {
      title: "Выплачено",
      dataIndex: "paid",
      key: "paid",
    },
    {
      title: "Остаток",
      key: "remaining",
      render: (_: any, cashTransfer: ICashTransferArchive) =>
        roundToThreeDecimals(cashTransfer.total - cashTransfer.paid),
    },
    {
      title: "Описание",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "C кассы",
      dataIndex: "fromCashboxName",
      key: "fromCashboxName",
    },
    {
      title: "На кассу",
      dataIndex: "toCashboxName",
      key: "toCashboxName",
    },
    {
      title: "Коммисия",
      dataIndex: "commission",
      key: "commission",
      render: (commission: number, record: ICashTransferArchive) => {
        return `${roundToThreeDecimals(commission)} (${
          record.commissionPercentage
        }%)`;
      },
    },
    {
      title: "Пользователь",
      dataIndex: "ownerUsername",
      key: "ownerUsername",
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title>Архив Конвертаций</Title>
      <RangePicker
        onChange={handleRangeChange}
        placeholder={["Начало", "Конец"]}
        format="DD.MM.YY"
        value={dateRange}
      />

      <Table
        columns={columns}
        dataSource={transfers}
        rowKey={(record) => record._id}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
          showSizeChanger: true,
        }}
        scroll={{ x: "max-content" }}
        loading={loading}
      />
    </div>
  );
}
